import React, { useState, useEffect } from "react";
import ExternalListingButton from "../../components/ExternalListingButton";
import ListingContactButtons from "../../components/ListingContactButtons";
import ListingActionBar from "../../components/ListingActionBar";
import { T } from "../Translate";
import {
  Container,
  CardMedia,
  Box,
  Typography,
  CircularProgress,
  Dialog,
  Button,
  IconButton,
  Card,
  CardContent,
  Chip,
  Stack,
  Grid,
} from "@mui/material";
import { ArrowBackIos as ArrowBackIosIcon } from "../../icons";
import { EventAvailable as EventAvailableIcon } from "../../icons";
import { ArrowForwardIos as ArrowForwardIosIcon } from "../../icons";
import { LocationOn as LocationOnIcon } from "../../icons";
import { PhotoLibrary as PhotoLibraryIcon } from "../../icons";
import { Flight as FlightIcon } from "../../icons";
import { Close as CloseIcon } from "../../icons";
import { doc, getDoc, setDoc, collection, serverTimestamp, query, getDocs, where } from "firebase/firestore";
import { db } from "../../firebase";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Header3 from "./TripsHeader";
import BottomNav from "../BottomToolbar";
import { isLoggedIn } from "../../session";
import { brandColors } from "../../theme";
import ListingImageSlideshow from "../../components/ListingImageSlideshow";
import FollowItemButton from "../../components/FollowItemButton";
import { pageContentSx } from "../../pageLayout";
import StoresAppBar from "../stores/StoresAppBar";
import GuestJoinBanner from "../../components/GuestJoinBanner";

const getYoutubeVideoId = (url) => {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  return match ? match[1] : null;
};

const CATEGORY_STYLES = {
  alojamiento: { color: "#4a90e2", bgcolor: "rgba(74,144,226,0.16)", label: <T>Alojamiento</T> },
  compra: { color: "#50e3c2", bgcolor: "rgba(80,227,194,0.16)", label: <T>Compra</T> },
  alquiler: { color: "#ed8936", bgcolor: "rgba(237,137,54,0.16)", label: <T>Alquiler</T> },
  combo: { color: "#9b51e0", bgcolor: "rgba(155,81,224,0.16)", label: <T>Combo Vuelo + Alojamiento</T> },
};

const PropertyDetail = () => {
  const { placeId } = useParams();
  const [place, setPlace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openImageModal, setOpenImageModal] = useState(false);
  const [authUserId, setAuthUserId] = useState(null);
  // null = todavía no se sabe si hay sesión. La página es pública: sin
  // sesión se ve igual (con barra simple e invitación a registrarse) y solo
  // "Consultar" pide iniciar sesión.
  const [isGuest, setIsGuest] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fetchAuthUserId = async () => {
      const user = await isLoggedIn();
      setAuthUserId(user ? user.uid : null);
      setIsGuest(!user);
    };
    fetchAuthUserId();
  }, []);

  useEffect(() => {
    const fetchPlace = async () => {
      try {
        const placeDocRef = doc(db, "places", placeId);
        const placeDoc = await getDoc(placeDocRef);

        if (placeDoc.exists()) {
          const placeData = placeDoc.data();
          setPlace({ id: placeDoc.id, ...placeData });
        } else {
          console.error("No se encontró el alojamiento.");
        }
      } catch (error) {
        console.error("Error al obtener el alojamiento:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPlace();
  }, [placeId]);

  const formatDate = (timeToDo) => {
    if (timeToDo instanceof Date) {
      return timeToDo.toLocaleDateString();
    } else if (timeToDo && timeToDo.toDate instanceof Function) {
      return timeToDo.toDate().toLocaleDateString();
    }
    return "";
  };

  const handleOpenImageModal = (index = 0) => {
    setCurrentImageIndex(index);
    setOpenImageModal(true);
  };

  const handleCloseImageModal = () => {
    setOpenImageModal(false);
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === place.images.length - 1 ? 0 : prevIndex + 1
    );
  };

  const handlePreviousImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? place.images.length - 1 : prevIndex - 1
    );
  };

  const handleChatClick = async () => {
    if (isGuest) {
      navigate("/login", { state: { from: location } });
      return;
    }
    if (!authUserId || !place) {
      console.error("Usuario no autenticado o lugar no cargado");
      return;
    }

    try {
      const conversationsRef = collection(db, "conversations");

      const q = query(
        conversationsRef,
        where("type", "==", "viaje"),
        where("itemId", "==", place.id),
        where("participants", "array-contains", authUserId)
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        const conversationId = [authUserId, place.uid, "viaje", place.id].sort().join("_");
        const conversationDocRef = doc(conversationsRef, conversationId);
        await setDoc(conversationDocRef, {
          participants: [authUserId, place.uid],
          type: "viaje",
          itemId: place.id,
          lastMessage: {
            content: "",
            timestamp: serverTimestamp(),
            senderId: authUserId
          },
          unreadCount: {
            [authUserId]: 0,
            [place.uid]: 0
          }
        });
      }

      navigate(`/chatbox/viaje/${place.id}/${place.uid}`);
    } catch (error) {
      console.error("Error al iniciar la conversación:", error);
    }
  };

  if (loading || isGuest === null) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!place) {
    return (
      <Container maxWidth={false} disableGutters sx={pageContentSx}>
        <Typography><T>No se encontró el alojamiento.</T></Typography>
      </Container>
    );
  }

  const images = Array.isArray(place.images) ? place.images : [];
  const categoryStyle = CATEGORY_STYLES[place.category];
  const isOwner = authUserId === place.uid;
  const youtubeId = getYoutubeVideoId(place.youtubeUrl);

  return (
    <Box sx={{ backgroundColor: brandColors.backgroundDefault, minHeight: "100vh" }}>
      {isGuest ? (
        <StoresAppBar title={<T>Viaje</T>} subtitle="Notiline" quickNav={false} />
      ) : (
        <Header3 />
      )}
      <Container sx={{ mt: isGuest ? 4 : 12, mb: 18, maxWidth: { xs: "100%", md: "980px" } }}>
        {isGuest && <GuestJoinBanner />}
        <Box
          onClick={() => images.length > 0 && handleOpenImageModal(0)}
          sx={{ position: "relative", width: "100%", mb: 2, cursor: images.length > 0 ? "pointer" : "default" }}
        >
          <ListingImageSlideshow
            images={images}
            alt={place.title}
            height={{ xs: 240, sm: 320 }}
            autoPlayMs={5000}
            PlaceholderIcon={FlightIcon}
            placeholderText={<T>No hay imágenes disponibles</T>}
          />
          {images.length > 1 && (
            <Chip
              icon={<PhotoLibraryIcon sx={{ color: "white !important" }} />}
              label={<T>Ver todas</T>}
              sx={{
                position: "absolute",
                top: 12,
                right: 12,
                bgcolor: "rgba(0,0,0,0.65)",
                color: "white",
                fontWeight: 600,
              }}
            />
          )}
        </Box>

        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} sx={{ mb: 1 }}>
          <Typography sx={{ fontSize: 24, fontWeight: 700, color: "text.primary", minWidth: 0, overflowWrap: "anywhere" }}>
            {place.title}
          </Typography>
          {categoryStyle && (
            <Chip
              size="small"
              label={categoryStyle.label}
              sx={{ flexShrink: 0, fontWeight: 600, color: categoryStyle.color, bgcolor: categoryStyle.bgcolor }}
            />
          )}
        </Stack>

        <Typography sx={{ fontSize: 26, fontWeight: 700, color: "success.main", mb: 0.5 }}>
          <T>$</T> {Number(place.price).toLocaleString()} <Typography component="span" sx={{ fontSize: 14, color: "text.secondary", fontWeight: 400 }}><T>por día</T></Typography>
        </Typography>

        <FollowItemButton type="place" item={{ id: placeId, ...place }} variant="button" sx={{ mb: 2 }} />

        {(place.address || place.city) && (
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 2, color: "text.secondary" }}>
            <LocationOnIcon sx={{ fontSize: 18 }} />
            <Typography sx={{ fontSize: 14 }}>{place.address || place.city}</Typography>
          </Stack>
        )}

        <Card sx={{ borderRadius: 3, backgroundColor: brandColors.backgroundPaper, border: "1px solid", borderColor: "divider", mb: 2 }}>
          <CardContent>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: "text.primary", mb: 1.5 }}>
              <T>Detalles</T>
            </Typography>
            <Grid container spacing={2}>
              {[
                { label: <T>Ciudad</T>, value: place.city },
                { label: <T>Lugar</T>, value: place.place },
              ].map((item, i) => (
                <Grid item xs={6} key={i}>
                  <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{item.label}</Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: "text.primary", overflowWrap: "anywhere" }}>
                    {item.value}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 3, backgroundColor: brandColors.backgroundPaper, border: "1px solid", borderColor: "divider", mb: 2 }}>
          <CardContent>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: "text.primary", mb: 1 }}>
              <T>Descripción</T>
            </Typography>
            <Typography sx={{ fontSize: 14, color: "text.secondary", whiteSpace: "pre-line" }}>
              {place.description}
            </Typography>
          </CardContent>
        </Card>

        {(place.video || youtubeId) && (
          <Card sx={{ borderRadius: 3, backgroundColor: brandColors.backgroundPaper, border: "1px solid", borderColor: "divider", mb: 2 }}>
            <CardContent>
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: "text.primary", mb: 1.5 }}>
                <T>Video</T>
              </Typography>
              {place.video && (
                <Box
                  component="video"
                  src={place.video}
                  controls
                  sx={{ width: "100%", borderRadius: 2, mb: youtubeId ? 2 : 0 }}
                />
              )}
              {youtubeId && (
                <Box sx={{ position: "relative", width: "100%", pt: "56.25%" }}>
                  <Box
                    component="iframe"
                    src={`https://www.youtube.com/embed/${youtubeId}`}
                    title="YouTube video"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    sx={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: 0, borderRadius: 2 }}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        <Typography sx={{ fontSize: 11, color: "text.disabled", mb: 2 }}>
          <T>Publicado el</T> {formatDate(place.createdAt)}
        </Typography>

        {/* Anuncio importado de otro sitio: se contacta en la publicación original. */}
        <ListingActionBar isGuest={isGuest}>
          {place.sourceUrl ? (
            <ExternalListingButton url={place.sourceUrl} />
          ) : (
            <>
              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={handleChatClick}
                disabled={isOwner}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  py: 1.5,
                  bgcolor: isOwner ? undefined : brandColors.primary,
                  "&:hover": { bgcolor: brandColors.primaryHover },
                }}
              >
                {isOwner ? <T>Este es tu anuncio</T> : <T>Consultar</T>}
              </Button>
              {!isOwner && place.category === "alojamiento" && place.paypalEmail && (
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  startIcon={<EventAvailableIcon />}
                  onClick={() => (isGuest ? navigate("/login", { state: { from: location } }) : navigate(`/trips/${place.id}/reservar`))}
                  sx={{ mt: 1, textTransform: "none", fontWeight: 600, py: 1.5, bgcolor: "success.main", "&:hover": { bgcolor: "success.dark" } }}
                >
                  <T>Reservar con PayPal</T>
                </Button>
              )}
              {!isOwner && (
                <ListingContactButtons
                  phone={place.whatsapp}
                  title={place.title}
                  isGuest={isGuest}
                  onRequireLogin={() => navigate("/login", { state: { from: location } })}
                />
              )}
            </>
          )}
        </ListingActionBar>
      </Container>

      <Dialog
        open={openImageModal}
        onClose={handleCloseImageModal}
        aria-label="Imágenes del alojamiento"
        fullScreen
        PaperProps={{ sx: { bgcolor: "#000" } }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 1,
            py: 1,
            position: "sticky",
            top: 0,
            zIndex: 1,
            bgcolor: "rgba(0,0,0,0.6)",
          }}
        >
          <IconButton onClick={handleCloseImageModal} sx={{ color: "white" }} aria-label="Cerrar">
            <CloseIcon />
          </IconButton>
          {images.length > 1 && (
            <Typography sx={{ color: "white", fontSize: 14 }}>
              {currentImageIndex + 1} / {images.length}
            </Typography>
          )}
          <Box sx={{ width: 40 }} />
        </Box>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            minHeight: 0,
            p: 2,
          }}
        >
          {images.length > 0 && (
            <>
              <CardMedia
                component="img"
                image={images[currentImageIndex]}
                alt={`Imagen ${currentImageIndex + 1} del alojamiento`}
                sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 2 }}
              />
              {images.length > 1 && (
                <>
                  <IconButton
                    onClick={handlePreviousImage}
                    sx={{ position: "absolute", left: 8, color: "white", bgcolor: "rgba(0,0,0,0.4)" }}
                  >
                    <ArrowBackIosIcon />
                  </IconButton>
                  <IconButton
                    onClick={handleNextImage}
                    sx={{ position: "absolute", right: 8, color: "white", bgcolor: "rgba(0,0,0,0.4)" }}
                  >
                    <ArrowForwardIosIcon />
                  </IconButton>
                </>
              )}
            </>
          )}
        </Box>
      </Dialog>

      {!isGuest && <BottomNav />}
    </Box>
  );
};

export default PropertyDetail;
